/* Catalog matching runs locally. No customer preferences leave the browser. */
(() => {
  function matchBooks(books, interest, budget) {
    const unique = new Map();
    for (const book of books) {
      if (interest !== 'all' && book.category !== interest) continue;
      if (budget > 0 && book.price > budget) continue;
      if (!unique.has(book.id)) unique.set(book.id, book);
    }
    // Diversify discovery across shelves without inventing ratings or reviews.
    const buckets = new Map();
    for (const book of unique.values()) {
      if (!buckets.has(book.category)) buckets.set(book.category, []);
      buckets.get(book.category).push(book);
    }
    const picks = [];
    while (picks.length < 3 && [...buckets.values()].some((items) => items.length)) {
      for (const items of buckets.values()) {
        if (items.length && picks.length < 3) picks.push(items.shift());
      }
    }
    return picks;
  }

  function surpriseBook(books, interest, budget, previousId, random = Math.random) {
    const unique = new Map();
    for (const book of books) {
      if (interest !== 'all' && book.category !== interest) continue;
      if (budget > 0 && book.price > budget) continue;
      if (!unique.has(book.id)) unique.set(book.id, book);
    }
    const eligible = [...unique.values()];
    const alternatives = eligible.filter(book => book.id !== previousId);
    const pool = alternatives.length ? alternatives : eligible;
    return pool.length ? [pool[Math.floor(random() * pool.length)]] : [];
  }

  // Exercise the matching rules with Node without adding a storefront dependency.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { matchBooks, surpriseBook };
    return;
  }
  if (customElements.get('tandoori-book-finder')) return;

  class TandooriBookFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const source = this.querySelector('[data-finder-data]');
      if (!source) return;
      try {
        this.catalog = JSON.parse(source.textContent);
      } catch {
        return;
      }
      this.initialized = true;
      this.querySelector('[data-find-button]').disabled = false;
      const surprise = this.querySelector('[data-surprise-button]');
      if (surprise) {
        surprise.disabled = false;
        surprise.addEventListener('click', () => {
          this.recommend(new FormData(this.querySelector('[data-finder-form]')), true);
        });
      }
      this.querySelector('[data-finder-form]').addEventListener('submit', (event) => {
        event.preventDefault();
        this.recommend(new FormData(event.currentTarget));
      });
      this.querySelector('[data-finder-form]').addEventListener('change', () => {
        // Previous picks no longer describe the newly selected preferences.
        this.querySelector('[data-finder-results]').hidden = true;
        clearTimeout(this.announceTimer);
        this.querySelector('[data-finder-announcer]').textContent = '';
        this.updateSummary();
      });
      this.querySelector('[data-finder-refine]')?.addEventListener('click', () => {
        const input = this.querySelector('input[name="interest"]:checked');
        input?.focus({preventScroll:true});
        this.querySelector('[data-finder-form]').scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',block:'center'});
      });
      this.updateSummary();
    }

    updateSummary() {
      const interest = this.querySelector('input[name="interest"]:checked')?.closest('label')?.textContent.trim();
      const budget = this.querySelector('select[name="budget"]')?.selectedOptions[0]?.textContent.trim();
      const summary = this.querySelector('[data-finder-summary]');
      if (summary) summary.textContent = [interest, budget].filter(Boolean).join(' · ');
    }

    recommend(preferences, isSurprise = false) {
      const interest = preferences.get('interest');
      const budget = Number(preferences.get('budget'));
      const picks = isSurprise
        ? surpriseBook(this.catalog.books, interest, budget, this.previousSurpriseId)
        : matchBooks(this.catalog.books, interest, budget);
      if (isSurprise && picks.length) this.previousSurpriseId = picks[0].id;
      const results = this.querySelector('[data-finder-results]');
      const cards = this.querySelector('[data-finder-cards]');
      const template = this.querySelector('[data-result-template]');
      cards.replaceChildren();
      for (const book of picks) {
        const card = template.content.cloneNode(true);
        const url = new URL(book.url, location.origin);
        if (url.origin !== location.origin) continue;
        for (const link of card.querySelectorAll('a')) link.href = url.href;
        card.querySelector('.tb-book__image').setAttribute('aria-label', book.title);
        card.querySelector('.tb-book__title a').textContent = book.title;
        card.querySelector('.tb-book__category').textContent = book.categoryTitle;
        card.querySelector('.tb-book__meta span').textContent = book.money;
        card.querySelector('.tb-book__reason').textContent = this.catalog.messages[isSurprise ? 'surprise_reason' : interest === 'all' ? 'explore' : 'match'];
        const image = card.querySelector('img');
        if (book.image) {
          image.src = book.image;
          image.alt = book.title;
        } else {
          image.remove();
        }
        const save = card.querySelector('[data-save-book]');
        if (save) Object.assign(save.dataset, {id:String(book.id),title:book.title,url:url.pathname,image:book.image || ''});
        cards.append(card);
      }
      results.hidden = false;
      results.classList.toggle('tb-finder__results--surprise', isSurprise && picks.length > 0);
      this.querySelector('[data-finder-heading]').textContent = this.catalog.messages[isSurprise && picks.length ? 'surprise_heading' : 'heading'];
      const message = picks.length
        ? isSurprise ? this.catalog.messages.surprise_count : this.catalog.messages.count.replace('[count]', String(picks.length))
        : this.catalog.messages.empty;
      this.querySelector('[data-finder-status]').textContent = message;
      // The live region sits outside the hidden results, so the first message is announced too.
      // Clearing it a moment before writing lets an identical repeat be announced again.
      const announcer = this.querySelector('[data-finder-announcer]');
      if (announcer) {
        announcer.textContent = '';
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => { announcer.textContent = message; }, 100);
      }
      this.querySelector('[data-finder-heading]').focus({ preventScroll: true });
      results.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest' });
    }
  }

  customElements.define('tandoori-book-finder', TandooriBookFinder);
})();
